import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { titleize } from 'underscore.string';
import { withTranslation } from 'react-i18next';
import { Button as UIButton, Card, CardContent, DataTable, } from '@cargomatic/ui';
import Container from 'components/v2/container';
import { FilterCardHeader } from 'components/design-system/molecules/FilterCardHeader';
import { FilterBarRegistry } from 'components/design-system/molecules/FilterBarRegistry';
import { TablePagination } from 'components/design-system/molecules/TablePagination';
import { ViewOptions } from 'components/design-system/molecules/ViewOptions';
import { useListPage, } from 'components/design-system/hooks/useListPage';
import { initialValuesFromConfigs } from 'components/design-system/hooks/useFilterState';
import DataPoller from 'components/ui/DataPoller';
import EquipmentActionCreators from 'actions/EquipmentActionCreators';
import AdminServerActionCreators from 'actions/AdminServerActionCreators';
import EquipmentCsvStore from 'stores/EquipmentCsvStore';
import SessionStore from 'stores/SessionStore';
import EditEquipmentModal from '../EditEquipmentModal';
import AddEquipmentModal from '../AddEquipmentModal';
import BulkEditEquipmentModal from '../BulkEditEquipmentModal';
import { makeEquipmentFilters } from './Equipment.filters';
import { fetchEquipment, buildEquipmentQuery, PER_PAGE, } from './fetcher';
import { getEquipmentColumns, EQUIPMENT_COLUMN_SPECS, TOGGLEABLE_COLUMN_IDS, } from './columns';
const CSV_LIMIT = 1000;
const PAGE_SIZE_OPTIONS = [50, 100, 200];
/**
 * Equipment list — v2 (canonical shipment-list-v2 pattern). Uses `useListPage`
 * (server pagination + sort) + the @cargomatic/ui `DataTable` driven by native
 * TanStack ColumnDefs (equipment/v2/columns), with the typed async fetcher
 * (equipment/v2/fetcher) reproducing v1's nested `dates` payload + single-bound
 * date rule.
 *
 * Reuses the legacy edit/add/bulk-edit modals, the CSV export pipeline
 * (AdminServerActionCreators.downloadEquipmentCsv + EquipmentCsvStore), and
 * column-visibility persistence (SessionStore equipment column preferences).
 * Behaviorally equivalent to `equipment/List.js`.
 */
function EquipmentListBase({ t }) {
    const FILTERS = useMemo(() => makeEquipmentFilters(), []);
    const initialValues = useMemo(() => initialValuesFromConfigs(FILTERS), [FILTERS]);
    // --- column visibility (persisted via SessionStore, matching v1) -------
    // v1 initial hidden set = stored prefs ∪ disabled columns. None of the
    // Mapping columns are disabled today, but the union is preserved for parity.
    const [hiddenColumns, setHiddenColumns] = useState(() => {
        const prefs = SessionStore.getEquipmentUserColumnPreferences() || [];
        const disabled = EQUIPMENT_COLUMN_SPECS.filter((c) => c.hideable === false && c.id !== 'equipment_id').map((c) => c.id);
        return Array.from(new Set([...prefs, ...disabled]));
    });
    // --- CSV ----------------------------------------------------------------
    const [downloadCSVDisabled, setDownloadCSVDisabled] = useState(false);
    // --- modals ------------------------------------------------------------
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showBulkEditModal, setShowBulkEditModal] = useState(false);
    const [activeEquipment, setActiveEquipment] = useState(null);
    // Snapshot the selected rows when bulk edit opens. `selectedRows` is rebuilt
    // (with fresh row objects) on every background poller refetch, which would
    // otherwise reset the modal's in-progress edits via its componentWillReceiveProps.
    const [bulkEditEquipments, setBulkEditEquipments] = useState([]);
    // Row click → open the edit modal (replaces v1's per-row `onClick` closure,
    // injected into the equipment_id link cell via the fetcher row shape).
    const fetcher = useCallback(async (params) => {
        const result = await fetchEquipment(params);
        return Object.assign(Object.assign({}, result), { items: result.items.map((e) => (Object.assign(Object.assign({}, e), { onClick: () => {
                    setActiveEquipment(e);
                    setShowEditModal(true);
                } }))) });
    }, []);
    const columns = useMemo(() => getEquipmentColumns({ selectable: true }), []);
    const list = useListPage({
        filters: FILTERS,
        initialValues,
        columns,
        fetcher,
        rowId: (r) => r._id,
        defaultPageSize: PER_PAGE,
    });
    const selectedRows = list.selectedRows;
    const bulkActionDisabled = selectedRows.length === 0;
    // --- store subscriptions (CSV download state) --------------------------
    useEffect(() => {
        const onCsvChange = () => {
            setDownloadCSVDisabled(!EquipmentCsvStore.isFileDownloaded());
        };
        EquipmentCsvStore.addChangeListener(onCsvChange);
        return () => EquipmentCsvStore.removeChangeListener(onCsvChange);
    }, []);
    useEffect(() => {
        document.title = 'Cargomatic Inc. - Equipment';
    }, []);
    // -----------------------------------------------------------------------
    // Column visibility — mirrors v1 `List.getColumnList`: every column is
    // user-toggleable EXCEPT the identity column `equipment_id`. Visibility is
    // synced onto the TanStack table AND persisted via SessionStore.
    // -----------------------------------------------------------------------
    const viewOptionsColumns = useMemo(() => EQUIPMENT_COLUMN_SPECS.filter((c) => TOGGLEABLE_COLUMN_IDS.includes(c.id)).map((c) => {
        var _a;
        return ({
            id: c.id,
            label: titleize(t(c.slug, { count: (_a = c.count) !== null && _a !== void 0 ? _a : 1 })),
            visible: !hiddenColumns.includes(c.id),
        });
    }), [hiddenColumns, t]);
    // Push the persisted hidden set into the table's column-visibility state.
    useEffect(() => {
        const visibility = {};
        for (const id of TOGGLEABLE_COLUMN_IDS) {
            visibility[id] = !hiddenColumns.includes(id);
        }
        list.table.setColumnVisibility(visibility);
    }, [hiddenColumns, list.table]);
    const handleToggleColumn = useCallback((id, visible) => {
        setHiddenColumns((prev) => {
            const next = visible
                ? prev.filter((f) => f !== id)
                : prev.includes(id)
                    ? prev
                    : [...prev, id];
            SessionStore.setEquipmentUserColumnPreferences(next);
            return next;
        });
    }, []);
    // -----------------------------------------------------------------------
    // CSV export — reuses the legacy CSV pipeline. `filters` is the built query
    // (incl. the nested `dates` payload), matching the prior v2 behavior.
    // -----------------------------------------------------------------------
    const handleExportToCSV = useCallback(() => {
        setDownloadCSVDisabled(true);
        const activeColumns = EQUIPMENT_COLUMN_SPECS.filter((c) => !hiddenColumns.includes(c.id)).map((c) => c.id);
        AdminServerActionCreators.downloadEquipmentCsv({
            filters: buildEquipmentQuery(list.values, list.page, list.pageSize, list.table.getState().sorting),
            limit: CSV_LIMIT,
            activeColumns,
        });
    }, [hiddenColumns, list.values, list.page, list.pageSize, list.table]);
    // -----------------------------------------------------------------------
    // Modal save handlers
    // -----------------------------------------------------------------------
    const saveEquipment = useCallback((entity) => {
        EquipmentActionCreators.addEquipment(entity);
    }, []);
    const updateEquipment = useCallback((entity) => {
        EquipmentActionCreators.updateEquipment(entity);
    }, []);
    // V1 stayed in sync via its EquipmentStore change listener; v2 reads
    // through useListPage, so refetch when a mutating modal closes. The modal
    // APIs don't distinguish save vs cancel, so a conservative refetch-on-close
    // beats leaving edited rows stale until the next poller tick.
    const handleHideEdit = useCallback(() => {
        setShowEditModal(false);
        list.refetch();
    }, [list.refetch]);
    const handleHideAdd = useCallback(() => {
        setShowAddModal(false);
        list.refetch();
    }, [list.refetch]);
    const handleHideBulkEdit = useCallback(() => {
        setShowBulkEditModal(false);
        list.table.resetRowSelection();
        list.refetch();
    }, [list.table, list.refetch]);
    return (React.createElement(Container, { className: "py-6", useMaxWidth: false, dataTestId: "equipment-list" },
        React.createElement(DataPoller, { action: list.refetch }),
        React.createElement(Card, null,
            React.createElement(FilterCardHeader, { title: titleize(t('EQUIPMENT')), onReset: list.reset, actions: React.createElement(React.Fragment, null,
                    React.createElement(UIButton, { id: "add-equipment-button", onClick: () => setShowAddModal(true) }, titleize(t('ADD_EQUIPMENT'))),
                    React.createElement(UIButton, { variant: "outline", disabled: bulkActionDisabled, title: t('SELECT_MULTIPLE_EQUIPMENT_TIP'), onClick: () => {
                            setBulkEditEquipments(selectedRows);
                            setShowBulkEditModal(true);
                        } }, t('BULK_EDIT')),
                    React.createElement(UIButton, { variant: "outline", disabled: downloadCSVDisabled, onClick: handleExportToCSV }, t('EXPORT_TO_CSV')),
                    React.createElement(ViewOptions, { columns: viewOptionsColumns, onToggleColumn: handleToggleColumn })) },
                React.createElement(FilterBarRegistry, { configs: FILTERS, values: list.values, labels: list.labels, displayed: list.displayed, onChange: list.setValue, onLabelChange: list.setLabel, onToggle: list.toggle })),
            React.createElement(CardContent, null,
                React.createElement(DataTable, { table: list.table, isLoading: list.isLoading, emptyResultsText: t('NO_DATA_FOUND') || 'No equipment found' }),
                React.createElement(TablePagination, { page: list.page, pages: list.totalPages, total: list.totalCount, perPage: list.pageSize, onPageChange: list.setPage, onPageSizeChange: list.setPageSize, pageSizeOptions: PAGE_SIZE_OPTIONS, selectedCount: selectedRows.length || undefined }))),
        showEditModal && activeEquipment && (React.createElement(EditEquipmentModal, { show: showEditModal, onHide: handleHideEdit, equipment: activeEquipment, onSave: updateEquipment })),
        showAddModal && (React.createElement(AddEquipmentModal, { show: showAddModal, onHide: handleHideAdd, onSave: saveEquipment })),
        showBulkEditModal && (React.createElement(BulkEditEquipmentModal, { show: showBulkEditModal, onHide: handleHideBulkEdit, equipments: bulkEditEquipments }))));
}
export const EquipmentList = withTranslation()(EquipmentListBase);
export default EquipmentList;
